import { Box, LinearProgress, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type Employee from '@src/Objects/Employee';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface ProfileCompetenciesSectionProps {
    employee: Employee;
}

function ProfileCompetenciesSection({ employee }: ProfileCompetenciesSectionProps) {
    const theme = useTheme();
    const navigate = useNavigate();

    // TODO: Check if competences from onboarding process (CV analysis) are properly mapped to employee.competences
    // Onboarding competences might be stored in EmployeeTalk.talkState instead of employee.competences
    // Need to merge both data sources or fix the mapping from onboarding to employee competences
    const competences = [...(employee.competences || [])].sort((a, b) => b.level - a.level).slice(0, 6);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
            }}
        >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Kompetenzen
            </Typography>

            {competences.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    Noch keine Kompetenzen definiert
                </Typography>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                        gap: 2,
                    }}
                >
                    {competences.map((competence) => (
                        <Box
                            key={competence.id}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                    '& .competence-title': {
                                        color: theme.palette.primary.main,
                                    },
                                },
                            }}
                            onClick={() => navigate(`/${ELinks.COMPETENCE}/${competence.id}`)}
                        >
                            <Box
                                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}
                            >
                                <Typography
                                    className="competence-title"
                                    variant="body2"
                                    sx={{ fontWeight: 600, transition: 'color 0.2s' }}
                                >
                                    {competence.title}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Level {competence.level}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={(competence.level / 5) * 100} // TODO: Make this dynamic based on levelDefinitions.length
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: theme.palette.primary.main,
                                        borderRadius: 3,
                                    },
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            )}
        </Paper>
    );
}

export default ProfileCompetenciesSection;
